import React, { useState } from 'react';
import { setAnalyticsIsEnabled, setIsPaying } from '@/services/profiles';
import { COUNTRY_CODES, DateDistanceDisplay, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { BygrProfileDetails, ProfileType } from '@/types';
import PrimaryCard from '@/components/Statistics/PrimaryCard';
import theme from './Profile.module.scss';
import { BooleanDisplay } from '@/components/Generic';
import EditProfileModal from './EditProfileModal';
import { Button, ConfirmButton, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import EmailTemplatesCard from '@/components/EmailTemplates/EmailTemplatesCard';

interface Props {
    profile: BygrProfileDetails;
    reloadProfile(): void;
}

const ProfileDashboard: React.FC<Props> = ({ profile, reloadProfile }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
    const handleChangeIsPaying = async () => {
        setIsLoading(true);
        await setIsPaying(profile!.id, !profile!.isPaying);
        setIsLoading(false);
        reloadProfile();
    };

    const handleToggleAnalytics = async () => {
        setIsLoading(true);
        await setAnalyticsIsEnabled(profile!.id, !profile!.analyticsIsEnabled);
        setIsLoading(false);
        reloadProfile();
    };

    if (!profile) return <LoadingIndicator />;

    return (
        <div className={theme.dashboard}>
            <PrimaryCard
                title={profile.name}
                actions={
                    profile.type === ProfileType.Seller && (
                        <Button size="sm" variant="tertiary" onClick={() => setIsEditProfileModalOpen(true)}>
                            Edit
                        </Button>
                    )
                }>
                <PrimaryCard.Row label="Number of members">{profile.members.length}</PrimaryCard.Row>

                {profile.type === ProfileType.Seller && (
                    <>
                        <PrimaryCard.Row label="Number of units">{profile.numberOfUnitsTotal}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Number of projects">{profile.projects.length}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Unit of measurement">{profile.defaultUnitOfMeasurement}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Country">{COUNTRY_CODES[profile.countryCode]}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Currency">
                            {profile.currency && `${profile.currency.name} (${profile.currency.code})`}
                        </PrimaryCard.Row>

                        <PrimaryCard.Row label="Is paying">
                            <BooleanDisplay value={profile.isPaying} />
                            &nbsp;
                            <ConfirmButton
                                size="xs"
                                loading={isLoading}
                                dialogTitle="Set is paying for profile"
                                dialogBody={`Are you sure you want to set is paying to ${!profile.isPaying}`}
                                onConfirm={handleChangeIsPaying}
                                variant="tertiary">
                                {profile.isPaying ? 'Disable' : 'Enable'}
                            </ConfirmButton>
                        </PrimaryCard.Row>
                        <PrimaryCard.Row label="Analytics enabled">
                            <BooleanDisplay value={profile.analyticsIsEnabled} />
                            &nbsp;
                            <SimpleTooltip content="Enables the embedded PowerBI 'Insights' reports in CM and AM for all members of this profile. Profile admins also get access to the Insights color theme settings.">
                                <ConfirmButton
                                    size="xs"
                                    loading={isLoading}
                                    dialogTitle="Set analytics for profile"
                                    dialogBody={`Are you sure you want to set analytics to ${!profile.analyticsIsEnabled}`}
                                    onConfirm={handleToggleAnalytics}
                                    variant="tertiary">
                                    {profile.analyticsIsEnabled ? 'Disable' : 'Enable'}
                                </ConfirmButton>
                            </SimpleTooltip>
                        </PrimaryCard.Row>
                    </>
                )}
                <PrimaryCard.Row label="Create date">
                    {profile.createDate && <DateDistanceDisplay value={profile.createDate} />}
                </PrimaryCard.Row>
            </PrimaryCard>

            {profile.type === ProfileType.Seller && <EmailTemplatesCard profileId={profile.id} />}

            <EditProfileModal
                profile={profile}
                isOpen={isEditProfileModalOpen}
                toggle={() => setIsEditProfileModalOpen(false)}
                onSave={reloadProfile}
            />
        </div>
    );
};

export default ProfileDashboard;
